import { API_BASE, fetchWithRetry } from "./config";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { initOfflineSubsystem } from './offline';
import { clearUserData } from './utils/clearUserData';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PushEnableNudge from './components/PushEnableNudge';
import { usePushNotifications } from './components/usePushNotifications';
import ProviderPortal from './ProviderPortal';
import TrainerPortal from './TrainerPortal';
import AIAssistantHub from './components/AIAssistant/AIAssistantHub';
import DietComplianceChecker from './components/DietComplianceChecker';
import WhatCanICook from './components/WhatCanICook';
import { printHTMLDocument } from './utils/printHelper';
import { authedBlobUrl, revokeBlobUrl, downloadAuthedFile } from './utils/authedBlobUrl';
import { isMobile as isMobileDevice } from './utils/platformDetect';
import TutorialsPage from './components/TutorialsPage';
import { CreditSpendProvider } from './contexts/CreditSpendContext';
import OnboardingInterview from './components/OnboardingInterview';
import TrialToast from './components/TrialToast';
import BetaBadge from './components/BetaBadge';
import FeedbackModal from './components/FeedbackModal';
import { initBetaTelemetry, setBetaScreen, recordApi, recordAction } from './utils/telemetry';
import AILimitModal from './components/AILimitModal';
import SubscriptionGate from './components/SubscriptionGate';
import TrendsAnalysis from './components/AIAssistant/TrendsAnalysis';
import LogHydrationModal from './components/forms/LogHydrationModal';
import HealthSummaryModal from './components/forms/HealthSummaryModal';
import AccountTab from './components/forms/AccountTab';
import SettingsTab from './components/forms/SettingsTab';
import DevicesTab from './components/forms/DevicesTab';
import MedicationsPage from './components/forms/MedicationsPage';
import RecordsPage from './components/forms/RecordsPage';
import HealthTab from './components/forms/HealthTab';
import NutritionTab from './components/forms/NutritionTab';
import ExerciseManagement from './components/forms/ExerciseManagement';
import DashboardTab from './components/forms/DashboardTab';
import MessageCenter from './components/MessageCenter';
import SupportChat from './components/SupportChat';
import DigitalTwinPage from './components/forms/DigitalTwinPage';
import LongevityPage from './components/forms/LongevityPage';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import { getOrCreateDeviceId, getDeviceNameFromUA, getOrCreateSessionId } from './utils/auth-helpers';
import { TenantProvider, useTenant } from './contexts/TenantContext';

// Authentication Context
export const AuthContext = React.createContext();

// =============================================================================
// IndexedDB Token Storage - Accessible by Service Worker
// =============================================================================
const TOKEN_DB_NAME = 'healist-auth';
const TOKEN_STORE = 'tokens';

function openTokenDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(TOKEN_DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(TOKEN_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function setTokenInDB(accessToken, refreshToken) {
  try {
    const db = await openTokenDB();
    const tx = db.transaction(TOKEN_STORE, 'readwrite');
    const store = tx.objectStore(TOKEN_STORE);
    store.put(accessToken, 'access_token');
    if (refreshToken) {
      store.put(refreshToken, 'refresh_token');
    }
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
    db.close();
  } catch (err) {
    console.error('IndexedDB write failed:', err);
  }
}

async function clearTokenInDB() {
  try {
    const db = await openTokenDB();
    const tx = db.transaction(TOKEN_STORE, 'readwrite');
    tx.objectStore(TOKEN_STORE).clear();
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
    db.close();
  } catch (err) {
    console.error('IndexedDB clear failed:', err);
  }
}

/**
 * ONE resolver for notification destinations, used by BOTH the warm path (the service worker
 * posting NAVIGATE into an already-open app) and the cold path (?navigate= on a fresh load).
 *
 * WHY IT IS SHARED — root cause, found 2026-08-07. These were two independent implementations
 * with DIFFERENT coverage: the warm path handled four URLs; the cold path handled exactly one
 * (health-summary). Everything else was silently dropped — including #89's own
 * /analytics?tab=objectives, which had no handler on either path. The cold path is the one a
 * PHONE almost always takes, because the PWA is usually not running, so on iOS a real reminder
 * opened Healist and then threw the destination away. Two implementations of one rule will
 * always drift; there is now one, and adding a destination means editing a single table.
 *
 * Accepts either form the callers carry: a full path ("/meal-plan", from postMessage) or the
 * bare deep-link segment ("meal-plan", from ?navigate=). Query strings are preserved so
 * "/analytics?tab=objectives" can select a sub-tab.
 *
 * Returns null only for genuinely empty input. An UNRECOGNISED destination resolves to the
 * dashboard rather than nothing — a tap must never do nothing, which is the entire defect
 * this function exists to end.
 */
export function resolveNotificationRoute(raw) {
  if (!raw) return null;
  const cleaned = String(raw).trim();
  if (!cleaned) return null;
  const withSlash = cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
  const [pathOnly, queryString] = withSlash.split('?');
  const params = new URLSearchParams(queryString || '');

  switch (pathOnly) {
    case '/':
    case '/dashboard':      return { tab: 'dashboard' };
    case '/meal-plan':      return { tab: 'nutrition' };
    case '/pantry':         return { tab: 'nutrition' };
    case '/medications':    return { tab: 'medications' };
    case '/devices':        return { tab: 'devices' };
    case '/digital-twin':   return { tab: 'digital-twin' };  // twin anomaly alerts (fix A, 2026-10-30)
    case '/health-summary': return { healthSummary: true };
    case '/analytics':
      return {
        tab: 'analytics',
        analyticsTab: params.get('tab') === 'objectives' ? 'objectives' : 'trends',
      };
    default:                return { tab: 'dashboard' };
  }
}

// getOrCreateDeviceId and getDeviceNameFromUA imported from utils/auth-helpers.js

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
      setIsAuthenticated(false);
    }
  }, [token]);

  // Mirror the live session into IndexedDB on EVERY load — not only at login/refresh.
  //
  // WHY (root cause of "Please open the app to log water", found 2026-08-06):
  // The service worker cannot read localStorage; IndexedDB is its ONLY view of the
  // session (sw.js:392 `_readTokensFromDB`). But `setTokenInDB` had exactly two callers —
  // authenticateWithTokens() on login and refreshToken() on rotation. A user who logged in
  // BEFORE the IndexedDB code shipped, or whose site data was cleared, therefore carried a
  // perfectly valid localStorage session while the SW saw NOTHING: `_authFetch` returned
  // null at sw.js:454 and every push action button fell through to its "open the app"
  // fallback. The action buttons are the entire point of the notification, so the feature
  // silently degraded to a manual task — and it looked like a push bug rather than an auth
  // one, which is why it went unexplained.
  //
  // Idempotent (put() overwrites) and cheap. Keyed on `token` so it also covers a token
  // changing by any path that bypasses those two helpers. On logout `token` goes null and
  // nothing is written — clearTokenInDB() has already run.
  useEffect(() => {
    const access = localStorage.getItem('token');
    if (!access) return;
    setTokenInDB(access, localStorage.getItem('refresh_token'));
  }, [token]);

  // Inactivity-gated auto-refresh — HIPAA §164.312(a)(2)(iii) compliance.
  // Tracks last user interaction. If inactive for 15 min, stops refreshing.
  // Access token expires 30 min after last refresh → user sees login screen.
  useEffect(() => {
    if (!token) return;

    const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes — HIPAA §164.312(a)(2)(iii) auto-logoff
    const REFRESH_INTERVAL_MS = 25 * 60 * 1000;  // 25 minutes
    const WATCHDOG_INTERVAL_MS = 60 * 1000;      // re-check user inactivity every minute
    let lastActivity = Date.now();
    let loggingOut = false;

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const onActivity = () => { lastActivity = Date.now(); };
    activityEvents.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));

    const refreshInterval = setInterval(() => {
      if (Date.now() - lastActivity < INACTIVITY_LIMIT_MS) {
        refreshToken();
      }
      // If inactive, do nothing here — the inactivity watchdog below performs the auto-logoff.
    }, REFRESH_INTERVAL_MS);

    // HIPAA §164.312(a)(2)(iii) auto-logoff. "Inactivity" must mean real USER inactivity
    // (no mouse/keyboard/touch/scroll), NOT API inactivity — background pollers keep hitting
    // the API every 60s and would otherwise keep an unattended session alive for up to 24h.
    // So on 15 min of no user interaction we terminate the session SERVER-SIDE (revoke the
    // device session via /logout) and then log out locally. The logout unmounts the
    // authenticated app, which stops every background poller. (#43, 2026-06-10)
    let inactivityWatchdog = null;
    const enforceInactivityLogout = () => {
      if (loggingOut) return;
      if (Date.now() - lastActivity < INACTIVITY_LIMIT_MS) return;
      loggingOut = true;
      clearInterval(refreshInterval);
      clearInterval(inactivityWatchdog);
      const t = localStorage.getItem('token');
      fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${t}`, 'X-Device-Id': getOrCreateDeviceId() },
      }).catch(() => { /* best-effort server revoke */ })
        .finally(() => handleSessionExpiry(true, 'inactivity'));
    };
    inactivityWatchdog = setInterval(enforceInactivityLogout, WATCHDOG_INTERVAL_MS);

    // Browsers throttle/freeze timers in backgrounded tabs, which can delay the interval
    // check. When the tab returns to the foreground, re-check immediately so a session that
    // crossed the inactivity threshold while backgrounded logs out at once. (#43, 2026-06-11)
    const onVisible = () => { if (document.visibilityState === 'visible') enforceInactivityLogout(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(inactivityWatchdog);
      document.removeEventListener('visibilitychange', onVisible);
      activityEvents.forEach(evt => window.removeEventListener(evt, onActivity));
    };
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        // Access token expired — attempt refresh before giving up
        console.log('Access token expired, attempting refresh...');
        const refreshed = await refreshToken();
        if (refreshed) {
          // Refresh succeeded — retry /auth/me with new token
          const newToken = localStorage.getItem('token');
          const retryResponse = await fetch(`${API_BASE}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${newToken}` }
          });
          if (retryResponse.ok) {
            const userData = await retryResponse.json();
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            handleSessionExpiry(true);
          }
        }
        // If refresh failed, handleSessionExpiry was already called inside refreshToken()
      } else {
        console.log('Unexpected auth response:', response.status);
        handleSessionExpiry(true);
      }
    } catch (error) {
      console.error('Failed to fetch user in App.jsx:', error);
      // Transient network/CDN drop (e.g., Cloudflare ↔ origin) — retry once before
      // logging out, so a blip doesn't strand the loader or force a re-login (#43 UAT).
      try {
        await new Promise((r) => setTimeout(r, 1200));
        const retry = await fetch(`${API_BASE}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (retry.ok) {
          setUser(await retry.json());
          setIsAuthenticated(true);
          return;
        }
      } catch (e2) { /* fall through to session expiry */ }
      handleSessionExpiry(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionExpiry = async (showBanner = true, reason = null) => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    // Preserve device_id across logouts — it's a stable browser identifier
    const deviceId = localStorage.getItem('healist_device_id');
    localStorage.clear();
    if (deviceId) localStorage.setItem('healist_device_id', deviceId);
    sessionStorage.clear();
    clearTokenInDB();
    // D11 (2026-10-21): the SW's cached health reads must not outlive the session (the next sign-in on this
    // browser saw the previous user's medications).
    clearUserData();

    if (reason === 'revoked') {
      setSessionExpiredReason('revoked');
    } else if (reason === 'inactivity') {
      setSessionExpiredReason('inactivity');
    } else if (showBanner) {
      setSessionExpiredReason('expired');
    }
  };

  const refreshToken = async () => {
  const refresh_token = localStorage.getItem('refresh_token');

  if (!refresh_token) {
    handleSessionExpiry(true);
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': getOrCreateDeviceId()
      },
      body: JSON.stringify({ refresh_token })
    });

    if (response.ok) {
      const data = await response.json();
      // Store new tokens (token rotation for security)
      setToken(data.access_token);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setTokenInDB(data.access_token, data.refresh_token);
      console.log('Token refreshed successfully');
      return true;
    } else {
      // Check if session was revoked (remote logout from another device)
      let reason = null;
      try {
        const errData = await response.json();
        if (errData.detail === 'Session revoked') reason = 'revoked';
      } catch (_) {}
      console.log('Token refresh failed, logging out');
      handleSessionExpiry(true, reason);
      return false;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    handleSessionExpiry(true);
    return false;
  }
};

  // Phase 2 MFA — shared helper to finalize login once we have full tokens.
  // Called from login(), MFA setup-verify, MFA challenge-verify, and backup-code flows.
  const authenticateWithTokens = async (tokenData) => {
    // D11 (2026-10-21): wipe any previous session's cached data BEFORE this user's screens mount. This also covers
    // a session that ended with the tab closed (no sign-out ran). Bounded wait (clearUserData caps it).
    await clearUserData();
    setToken(tokenData.access_token);
    localStorage.setItem('token', tokenData.access_token);
    localStorage.setItem('refresh_token', tokenData.refresh_token);
    setTokenInDB(tokenData.access_token, tokenData.refresh_token);
    setIsAuthenticated(true);
  };

  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'X-Device-Id': getOrCreateDeviceId(),
          'X-Device-Name': getDeviceNameFromUA(),
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        // Retroactive password strength check — weak password needs changing
        if (data.must_change_password) {
          return {
            success: false,
            must_change_password: true,
            temp_token: data.temp_token,
            reason: data.reason || 'Please update your password to meet our security requirements.',
          };
        }

        // Phase 2 — MFA branch responses arrive with 200 + requires_mfa[_setup]
        if (data.requires_mfa_setup) {
          return {
            success: false,
            requires_mfa_setup: true,
            temp_token: data.temp_token,
            expires_in: data.expires_in,
          };
        }
        if (data.requires_mfa) {
          return {
            success: false,
            requires_mfa: true,
            mfa_method: data.mfa_method,
            backup_only: !!data.backup_only,
            temp_token: data.temp_token,
            expires_in: data.expires_in,
          };
        }

        // Normal path — tokens in hand (await: authenticateWithTokens clears the previous session's cache first)
        await authenticateWithTokens(data);
        console.log('Login successful, tokens stored');
        return { success: true };
      } else {
        // 401 with locked-out detail = MFA lockout
        if (response.status === 401 && data.detail && data.detail.locked) {
          return {
            success: false,
            locked: true,
            locked_until: data.detail.locked_until,
            error: data.detail.message || 'Account temporarily locked',
          };
        }
        return { success: false, error: data.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error in App.jsx - login(). Please check your connection and try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
    
      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: data.detail || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error in App.jsx - register(). Please check your connection and try again.' };
    }
  };

  const logout = async (showAlert = false) => {
    try {
      // Call backend logout to invalidate THIS device's session only
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Device-Id': getOrCreateDeviceId()
          }
        });
      }
    } catch (error) {
      console.error('Logout request failed in App.jsx:', error);
    } finally {
      // Always clear local state regardless of backend response
      // Preserve device_id — it's a stable browser identifier
      const deviceId = localStorage.getItem('healist_device_id');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.clear();
      if (deviceId) localStorage.setItem('healist_device_id', deviceId);
      sessionStorage.clear();
      clearTokenInDB();
      // D11 (2026-10-21): the SW cache + offline stores too — this is what makes the message below true.
      await clearUserData();

      if (showAlert) {
        alert('You have been logged out successfully. All cached data has been cleared.');
      }
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Force logout after password change
        await logout();
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.detail || 'Password change failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error in App.jsx - changePassword(). Please try again.' };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      authenticateWithTokens,
      isAuthenticated,
      sessionExpiredReason,
      clearSessionExpiredReason: () => setSessionExpiredReason(null),
      register,
      logout,
      changePassword,
      refreshToken,
      refreshUser: fetchCurrentUser,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
}
// ============================================
// DASHBOARD COMPONENT REPLACEMENT
// Replace lines 1374-1563 in App.jsx
// ============================================

function Dashboard() {
  const [healthStatus, setHealthStatus] = useState(null);
  const [activeTab, setActiveTabState] = useState('dashboard');
  const [showFeedback, setShowFeedback] = useState(false);      // #83 — beta feedback dialog
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [nutritionRefreshKey, setNutritionRefreshKey] = useState(0);
  const [aiAgentInitialTool, setAiAgentInitialTool] = useState(null);
  // M-17 (Dev audit 2026-10-20): the AI Consult hub renders INSTEAD of every tab while it is open, so a
  // side-menu choice used to change the header but leave the hub on screen. Every way of changing tab
  // (side menu, notification deep links, a page's own links) goes through here, so going to a tab always
  // shows that tab — including re-choosing the tab that is already active.
  const setActiveTab = useCallback((tab) => {
    setShowAIAssistant(false);
    setAiAgentInitialTool(null);
    setActiveTabState(tab);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [onboardingOffer, setOnboardingOffer] = useState(null);  // #64 first-login onboarding
  const [showHealthSummary, setShowHealthSummary] = useState(false);
  // Which sub-tab Analytics should open on. Set by a notification deep link
  // (/analytics?tab=objectives, the #89 achievement prompt); 'trends' otherwise.
  const [analyticsInitialTab, setAnalyticsInitialTab] = useState('trends');
  const [connectivityMode, setConnectivityMode] = useState('ONLINE'); // P-007 connectivity mode
  const [showDietCheck, setShowDietCheck] = useState(false);
  const [showWhatCanICook, setShowWhatCanICook] = useState(false);
  const [showHydrationModal, setShowHydrationModal] = useState(false);
  const [wciRecipeView, setWciRecipeView] = useState(null);
  // H7: the recipe view is fetched with an auth header and rendered from a blob: URL, so the
  // iframe src is this object URL rather than an API URL carrying the session token.
  const [wciRecipeUrl, setWciRecipeUrl] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const { user, token, refreshToken } = React.useContext(AuthContext);
  const tenant = useTenant();

  useEffect(() => {
    fetchHealthStatus();
    fetchCreditInfo();
  }, []);

  // #80.1 — refresh credits on tab focus/visibility instead of a blind 60s poll.
  // The idle poll fired during inactive gaps and landed on a stale reused upstream connection →
  // intermittent no-ACAO / ERR_FAILED console noise (browser logs it even though fetchWithRetry
  // recovers). Event-driven = zero idle traffic, immune to keep-alive drift, demo-clean.
  useEffect(() => {
    if (!token) return;
    const refreshOnActive = () => {
      if (document.visibilityState === 'visible') fetchCreditInfo();
    };
    window.addEventListener('focus', refreshOnActive);
    document.addEventListener('visibilitychange', refreshOnActive);
    return () => {
      window.removeEventListener('focus', refreshOnActive);
      document.removeEventListener('visibilitychange', refreshOnActive);
    };
  }, [token]);

  const fetchCreditInfo = async () => {
    if (!token) return;
    try {
      const res = await fetchWithRetry(() => fetch(`${API_BASE}/api/profile/ai-usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }));
      if (res.status === 401) return;
      if (res.ok) setCreditInfo(await res.json());
    } catch (e) { /* silent — non-critical */ }
  };

  // Make Recipe handler for WCI recipe viewer (same logic as NutritionTab's handleMakeRecipe)
  const handleWciMakeRecipe = async (recipeId, recipeName) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/pantry/user/make-recipe/${recipeId}`,
        { method: 'POST' }
      );
      if (response.status === 401) {
        alert('Session expired. Please refresh the page and try again.');
        return;
      }
      if (response.ok) {
        const result = await response.json();
        const lines = [`Recipe: ${result.recipe_name || recipeName || 'Done'}\n`];
        if (result.items_decremented?.length > 0) {
          lines.push('Used from pantry:');
          result.items_decremented.forEach(i => lines.push(`  ${i.name}: ${i.previous_qty} → ${i.new_qty}`));
        }
        if (result.items_depleted?.length > 0) {
          lines.push('\nUsed up completely:');
          result.items_depleted.forEach(i => lines.push(`  ${i.name} (was ${i.previous_qty})`));
        }
        if (result.items_missing?.length > 0) {
          lines.push('\nNot in pantry:');
          result.items_missing.forEach(i => lines.push(`  ${i.name} (${i.quantity} ${i.unit})`));
        }
        if (result.items_opened?.length > 0) {
          lines.push('\nOpened (new expiry):');
          result.items_opened.forEach(i => lines.push(`  ${i.name} — use by ${i.new_expiry}`));
        }
        if (result.items_thawed?.length > 0) {
          lines.push('\nAuto-thawed:');
          result.items_thawed.forEach(i => lines.push(`  ${i.name} — use within ${i.days_to_use} days`));
        }
        alert(lines.join('\n'));
      } else {
        const errData = await response.json().catch(() => ({}));
        const detail = errData.detail || 'Failed to update pantry inventory';
        alert(typeof detail === 'string' ? detail : detail.map(d => d.msg || d).join(', '));
      }
    } catch (error) {
      console.error('Error making recipe:', error);
      alert('Error updating pantry inventory: ' + error.message);
    }
  };

  // ── H7 (2026-08-04): recipe viewer over an authenticated fetch + blob: URL ────────────────
  // Previously the iframe src was `/api/recipes/{id}/view?token=${localStorage.token}`, which put
  // a live session credential into a URL — access logs, browser history, Referer — and kept
  // working from anywhere it leaked until the token expired. Now the HTML is fetched with an
  // Authorization header and handed to the iframe as an in-memory blob, so there is no URL to
  // leak. The generated HTML is self-contained (the recipe image is inlined as a data: URI),
  // which is what makes a blob's opaque origin safe here.
  // A PDF attachment cannot be embedded inside the generated HTML (Chrome blocks data: URLs for
  // frame navigation) but renders fine as a blob: URL here, so fetch the PDF itself rather than
  // render a card telling the user their own file is unavailable.
  const openWciRecipeView = async (recipe) => {
    revokeBlobUrl(wciRecipeUrl);
    setWciRecipeUrl(null);
    setWciRecipeView(recipe);
    const isPdf = recipe.mime_type === 'application/pdf' && !!recipe.file_path;
    const url = isPdf
      ? `${API_BASE}/api/recipes/${recipe.id}/download`
      : `${API_BASE}/api/recipes/${recipe.id}/view`;
    try {
      setWciRecipeUrl(await authedBlobUrl(fetchWithAuth, url));
    } catch (error) {
      console.error('Failed to load recipe view:', error);
    }
  };

  // The only way to get a recipe attachment back out of the app. Before 2026-08-04 there was none:
  // the viewer's download links pointed at /api/recipes/{id}/file, which does not exist, and the
  // real endpoint had no caller and was itself broken.
  const handleWciDownloadFile = async (recipe) => {
    const name = (recipe.file_path || '').split('/').pop() || `${recipe.title || 'recipe'}`;
    try {
      await downloadAuthedFile(fetchWithAuth, `${API_BASE}/api/recipes/${recipe.id}/download`, name);
    } catch (error) {
      console.error('Failed to download recipe file:', error);
      alert('Failed to download the attached file.');
    }
  };

  // Every close path must go through here: a blob URL pins its bytes for the life of the document
  // unless revoked, so an un-revoked viewer leaks memory for the whole session.
  const closeWciRecipeView = () => {
    revokeBlobUrl(wciRecipeUrl);
    setWciRecipeUrl(null);
    setWciRecipeView(null);
  };

  // Print WCI recipe via fetch + printHelper (avoids cross-origin iframe restriction)
  const handleWciPrint = async (recipeId, recipeTitle) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/recipes/${recipeId}/view`
      );
      if (response.ok) {
        const html = await response.text();
        printHTMLDocument(html, recipeTitle);
      } else {
        alert('Failed to load recipe for printing');
      }
    } catch (error) {
      console.error('Error printing recipe:', error);
      alert('Error printing recipe');
    }
  };

  // Close floating modals on sidebar navigation
  useEffect(() => {
    setShowWhatCanICook(false);
    setShowHealthSummary(false);
    setShowDietCheck(false);
    setShowHydrationModal(false);
  }, [activeTab]);

  // Listen for Service Worker NAVIGATE messages (push notification deep links)
  // Uses both postMessage (controlled clients) and BroadcastChannel (reliable fallback)
  useEffect(() => {
    const handleNavigate = (url) => {
      const target = resolveNotificationRoute(url);
      if (!target) return;
      if (target.healthSummary) {
        setShowHealthSummary(true);
        return;
      }
      if (target.analyticsTab) setAnalyticsInitialTab(target.analyticsTab);
      if (target.tab) setActiveTab(target.tab);
    };

    // Primary: SW postMessage (only works when SW controls the page)
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NAVIGATE') {
        handleNavigate(event.data.url || '/');
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }

    // Backup: BroadcastChannel (works regardless of SW control state)
    let bc;
    try {
      bc = new BroadcastChannel('sw-navigate');
      bc.onmessage = (event) => {
        if (event.data?.type === 'NAVIGATE') {
          handleNavigate(event.data.url || '/');
        }
      };
    } catch (e) { /* BroadcastChannel not supported */ }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
      if (bc) bc.close();
    };
  }, []);

  // P-007 — initialize the offline-first subsystem (connectivity detection + intelligent sync +
  // encrypted offline store + SW-timer offline alerts). Idempotent; exposes window.__healistOffline
  // for offline-aware call sites and E2E verification.
  useEffect(() => {
    try {
      const offline = initOfflineSubsystem({ probeUrl: `${API_BASE}/api/health` });
      window.__healistOffline = offline;
      const unsub = offline.subscribe((mode) => setConnectivityMode(mode));
      setConnectivityMode(offline.getMode());
      return () => { unsub && unsub(); };
    } catch (e) {
      // Offline subsystem is best-effort; never block app startup.
    }
  }, []);

  // Handle a notification deep link on initial load — the app was CLOSED when the notification
  // was tapped. This is the path a PHONE almost always takes, and until 2026-08-07 it handled
  // exactly one destination (health-summary) and silently dropped every other one.
  //
  // Deliberately gated on `user`: arriving while signed OUT leaves ?navigate= in the URL
  // untouched, so the destination survives the login round-trip — this effect re-runs when
  // `user` becomes set and applies it then. Consuming it earlier would drop someone on the
  // login screen and forget where they were going, which is the dead-end pattern again.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const deepLink = params.get('navigate');
    if (!deepLink) return;

    const target = resolveNotificationRoute(deepLink);
    if (target) {
      if (target.healthSummary) {
        setShowHealthSummary(true);
      } else {
        if (target.analyticsTab) setAnalyticsInitialTab(target.analyticsTab);
        if (target.tab) setActiveTab(target.tab);
      }
    }

    // Consume ONLY `navigate` so a refresh doesn't re-navigate. Rebuilding the URL from
    // pathname alone would also discard ?invite=1, which the beta gate depends on.
    params.delete('navigate');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, [user]);

  useEffect(() => {
    // Show PWA install prompt on first login (mobile only)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    if (isMobileDevice && !isPWA && user && !user.pwa_install_prompted) {
      setTimeout(() => {
        setShowPWAPrompt(true);
      }, 2000);
    }
  }, [user]);

  // #64 — first-login onboarding: the server decides whether to offer (skippable, ≤3 re-prompts,
  // dismiss-able). Fires once the user is loaded; the overlay renders only when the server says offer.
  useEffect(() => {
    if (!user || onboardingOffer) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetchWithAuth(`${API_BASE}/api/interview/should-offer?consumer=onboarding`);
        if (!r.ok || !alive) return;
        const d = await r.json();
        if (d.offer) setOnboardingOffer(d);
      } catch { /* never block app load on the onboarding check */ }
    })();
    return () => { alive = false; };
  }, [user]);

  const fetchHealthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/health`);
      const data = await response.json();
      setHealthStatus(data);
    } catch (error) {
      console.error('Failed to fetch health status:', error);
    }
  };

  const fetchWithAuth = async (url, options = {}) => {
    const doFetch = (authToken) => fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'X-Device-Id': getOrCreateDeviceId(),
        'X-Session-Id': getOrCreateSessionId(),   // #83 correlation spine
        ...options.headers
      }
    });

    // #83 telemetry timing (no-op for non-beta; never affects the request)
    const _t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();

    // Always read token from localStorage (not React state) to avoid
    // stale closure in setInterval-based pollers (MessageCenter, etc.)
    let response = await doFetch(localStorage.getItem('token'));

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem('token');
        response = await doFetch(newToken);
      }
    }

    // Subscription lapsed mid-session: the backend paywall now 403s app endpoints. Re-fetch
    // the user so the SubscriptionGate renders immediately instead of waiting for the next
    // poll. /api/auth/me is paywall-exempt, so fetchCurrentUser() cannot loop on this. (#43)
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.detail?.code === 'subscription_required') { fetchCurrentUser(); }
      } catch { /* non-JSON 403 — ignore */ }
    }

    // #83 telemetry (metadata only; no-op unless a beta user initialized the collector)
    try {
      const _dur = ((typeof performance !== 'undefined') ? performance.now() : Date.now()) - _t0;
      recordApi(options.method || 'GET', url, response.status, _dur, response.headers.get('X-Request-Id'));
    } catch { /* telemetry must never affect the request */ }

    return response;
  };

  const pushNotifications = usePushNotifications(fetchWithAuth, API_BASE);

  // #83 Phase C — beta telemetry: init the collector (beta-only; no-op for everyone else) + route breadcrumbs.
  useEffect(() => {
    if (user?.is_beta) initBetaTelemetry(fetchWithAuth);
  }, [user?.is_beta]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user?.is_beta) { setBetaScreen(activeTab); recordAction('route', activeTab); }
  }, [activeTab, user?.is_beta]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWithAuthFormData = async (url, formData) => {
    const doFetch = (authToken) => fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'X-Device-Id': getOrCreateDeviceId()
      },
      body: formData
    });

    let response = await doFetch(localStorage.getItem('token'));

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem('token');
        response = await doFetch(newToken);
      }
    }

    // Subscription lapsed mid-session → render the gate immediately (see fetchWithAuth). (#43)
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.detail?.code === 'subscription_required') { fetchCurrentUser(); }
      } catch { /* non-JSON 403 — ignore */ }
    }

    return response;
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  const getPageTitle = () => {
    // One entry per activeTab route key, ordered to mirror the sidebar sections
    // (Home / Wellness / Medical / My Account) so this map stays auditable against
    // the sidebar nav below (HOME SECTION → end of MY ACCOUNT SECTION). Every key here
    // must exist there and vice versa —
    // a missing key silently falls through to 'Dashboard', which is the bug this
    // map had for analytics/devices/digital-twin/longevity/medications/records/settings.
    const titles = {
      // Home
      dashboard: 'Health Overview',
      analytics: 'Analytics & Reporting',
      // Wellness
      nutrition: 'Nutrition',
      exercise: 'Fitness',
      devices: 'Devices',
      // Medical
      health: 'Health Management',
      'digital-twin': 'Digital Twin',
      longevity: 'Longevity',
      medications: 'Medications',
      records: 'Records',
      // My Account
      settings: 'Settings',
      account: 'Account Settings',
      tutorials: 'Tutorials'
    };
    return titles[activeTab] || 'Dashboard';
  };

  return (
    <div className="dashboard">
      {/* P-007 — offline/degraded connectivity banner (driven by the connectivity detection module) */}
      {connectivityMode !== 'ONLINE' && (
        <div
          className="offline-banner"
          data-testid="offline-banner"
          role="status"
          style={{
            background: connectivityMode === 'OFFLINE' ? '#8a1c1c' : '#8a6d1c',
            color: '#fff', textAlign: 'center', padding: '6px 12px', fontSize: 13, fontWeight: 600,
          }}
        >
          {connectivityMode === 'OFFLINE'
            ? 'Offline — your changes are saved on this device and will sync when you reconnect.'
            : 'Limited connectivity — syncing critical health data first.'}
        </div>
      )}
      {/* TOP HEADER BAR - Dark Navy */}
      <header className="top-header">
        <div className="top-header-left">
          <button 
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-icon">☰</span>
            <span className="hamburger-text">MENU</span>
          </button>
          <div className="brand-logo">
            <div className="brand-mark">
              <img src={tenant.logo_url || "/icon-96x96.png"} alt={tenant.site_name} />
            </div>
            <div className="brand-name">{tenant.is_default_tenant ? <>Healist<span>.AI</span></> : tenant.site_name}</div>
          </div>
        </div>
        <div className="top-header-center">
          <SupportChat fetchWithAuth={fetchWithAuth} currentPage={activeTab || 'dashboard'} />
        </div>
        <div className="top-header-right">
          <div className="header-title">
            <h1>{getPageTitle()}</h1>
            <p>{getCurrentDate()}</p>
          </div>
          <MessageCenter fetchWithAuth={fetchWithAuth} />
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-sidebar-overlay visible"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* AI CONSULT — Elevated CTA above all sections */}
        <button
          className="ai-cta"
          onClick={() => { setShowAIAssistant(true); setMobileMenuOpen(false); }}
        >
          <span className="ai-cta-icon">🧠</span>
          <span>AI Consult</span>
          <span className="ai-cta-badge">6 Tools</span>
        </button>

        <nav className="sidebar-nav">
          {/* HOME SECTION */}
          <div className="nav-section">
            <div className="nav-section-title">Home</div>
            <button
              className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-text">Dashboard</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => { setActiveTab('analytics'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">📈</span>
              <span className="nav-text">Analytics & Reporting</span>
            </button>
          </div>

          {/* WELLNESS SECTION */}
          <div className="nav-section">
            <div className="nav-section-title">Wellness</div>
            <button
              className={`nav-item ${activeTab === 'nutrition' ? 'active' : ''}`}
              onClick={() => { setActiveTab('nutrition'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">🍎</span>
              <span className="nav-text">Nutrition</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'exercise' ? 'active' : ''}`}
              onClick={() => { setActiveTab('exercise'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">🏋️</span>
              <span className="nav-text">Fitness</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'devices' ? 'active' : ''}`}
              onClick={() => { setActiveTab('devices'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">⌚</span>
              <span className="nav-text">Devices</span>
            </button>
          </div>

          {/* MEDICAL SECTION */}
          <div className="nav-section">
            <div className="nav-section-title">Medical</div>
            <button
              className={`nav-item ${activeTab === 'health' ? 'active' : ''}`}
              onClick={() => { setActiveTab('health'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">❤️</span>
              <span className="nav-text">Health</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'digital-twin' ? 'active' : ''}`}
              onClick={() => { setActiveTab('digital-twin'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">🧬</span>
              <span className="nav-text">Digital Twin</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'longevity' ? 'active' : ''}`}
              onClick={() => { setActiveTab('longevity'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">✨</span>
              <span className="nav-text">Longevity</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'medications' ? 'active' : ''}`}
              onClick={() => { setActiveTab('medications'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">💊</span>
              <span className="nav-text">Medications</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'records' ? 'active' : ''}`}
              onClick={() => { setActiveTab('records'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">🩺</span>
              <span className="nav-text">Records</span>
            </button>
          </div>

          {/* MY ACCOUNT SECTION */}
          <div className="nav-section">
            <div className="nav-section-title">My Account</div>
            <button
              className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">Settings</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => { setActiveTab('account'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">👤</span>
              <span className="nav-text">Account</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'tutorials' ? 'active' : ''}`}
              onClick={() => { setActiveTab('tutorials'); setMobileMenuOpen(false); }}
            >
              <span className="nav-icon">📖</span>
              <span className="nav-text">Tutorials</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          {creditInfo && !creditInfo.is_test_account && creditInfo.daily_credits && (
            <div className="sbm-credits" onClick={() => { setActiveTab('account'); setMobileMenuOpen(false); }}>
              <div className="sbm-credits-bar">
                <div
                  className="sbm-credits-fill"
                  style={{ width: `${(creditInfo.daily_credits.total || 0) > 0 ? Math.min(100, ((creditInfo.daily_credits.remaining || 0) / creditInfo.daily_credits.total) * 100) : 0}%` }}
                />
              </div>
              <div className="sbm-credits-label">
                <span>{creditInfo.daily_credits.remaining || 0} / {creditInfo.daily_credits.total || 0} credits</span>
                <span className="sbm-credits-plan">{creditInfo.tier_label || ''} Plan</span>
              </div>
              {(creditInfo.purchased_credits || 0) > 0 && (
                <div className="sbm-credits-purchased">+{creditInfo.purchased_credits} purchased</div>
              )}
            </div>
          )}
          {creditInfo && creditInfo.is_test_account && (
            <div className="sbm-credits sbm-credits-unlimited" onClick={() => { setActiveTab('account'); setMobileMenuOpen(false); }}>
              <div className="sbm-credits-label">
                <span>Unlimited Credits</span>
                <span className="sbm-credits-plan">Test Account</span>
              </div>
            </div>
          )}
          {user?.is_beta && (
            <button
              type="button"
              className="sbm-feedback-btn"
              onClick={() => setShowFeedback(true)}
              title="Share beta feedback"
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', margin: '8px 0',
                padding: '8px 12px', borderRadius: 10, border: '1px solid var(--brand-gold,#d4a843)',
                background: 'transparent', color: 'var(--brand-text)', cursor: 'pointer', fontSize: 13,
              }}
            >
              <span>🧪</span> Send Beta Feedback
            </button>
          )}
          <div className="user-profile" onClick={() => setActiveTab('account')}>
            <div className="user-avatar">{getUserInitials()}</div>
            <div className="user-info">
              <div className="user-name">{user?.email?.split('@')[0] || 'User'}<BetaBadge user={user} /></div>
              <div className="user-tier">{creditInfo?.tier_label || 'Trial'} Plan</div>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="main-content">
        <TrialToast user={user} onGoToAccount={() => setActiveTab('account')} />
        {showFeedback && (
          <FeedbackModal
            fetchWithAuth={fetchWithAuth}
            currentScreen={activeTab}
            onClose={() => setShowFeedback(false)}
          />
        )}
        {showAIAssistant ? (
          <AIAssistantHub
            key={`ai-assistant-${aiAgentInitialTool || 'default'}`}
            user={user}
            token={token}
            fetchWithAuth={fetchWithAuth}
            initialTool={aiAgentInitialTool}
            onViewRecipe={(recipe) => {
              setShowAIAssistant(false);
              setAiAgentInitialTool(null);
              openWciRecipeView(recipe);
            }}
            onClose={() => {
              setShowAIAssistant(false);
              setAiAgentInitialTool(null);
            }}
            onSuccess={() => {
              setShowAIAssistant(false);
              setAiAgentInitialTool(null);
              setNutritionRefreshKey(prev => prev + 1);
              setActiveTab('nutrition');
            }}
          />
        ) : (
          <>
        {activeTab === 'dashboard' && (
          <DashboardTab
            fetchWithAuth={fetchWithAuth}
            user={user}
            setShowHealthSummary={setShowHealthSummary}
            setShowDietCheck={setShowDietCheck}
            setShowHydrationModal={setShowHydrationModal}
            setShowWhatCanICook={setShowWhatCanICook}
            setShowAIAssistant={setShowAIAssistant}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'health' && <HealthTab fetchWithAuth={fetchWithAuth} token={token} setActiveTab={setActiveTab} />}
        {activeTab === 'nutrition' && (
          <NutritionTab
            fetchWithAuth={fetchWithAuth}
            setShowAIAssistant={setShowAIAssistant}
            fetchWithAuthFormData={fetchWithAuthFormData}
            setAiAgentInitialTool={setAiAgentInitialTool}
            refreshKey={nutritionRefreshKey}
            token={token}
            setActiveTab={setActiveTab}
            setShowWhatCanICook={setShowWhatCanICook}
          />
        )}
        {activeTab === 'exercise' && (
          <ExerciseManagement
            fetchWithAuth={fetchWithAuth}
            fetchWithAuthFormData={fetchWithAuthFormData}
            setShowAIAssistant={setShowAIAssistant}
            setAiAgentInitialTool={setAiAgentInitialTool}
            token={token}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'account' && <AccountTab fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
{activeTab === 'settings' && <SettingsTab fetchWithAuth={fetchWithAuth} pushNotifications={pushNotifications} token={token} setActiveTab={setActiveTab} />}
        {/* Profile route removed — content dissolved into Health (Body, Score) and Fitness (Sports) */}
        {activeTab === 'devices' && <DevicesTab fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
        {activeTab === 'records' && <RecordsPage fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
        {activeTab === 'digital-twin' && <DigitalTwinPage fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
        {activeTab === 'longevity' && <LongevityPage fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
        {activeTab === 'medications' && <MedicationsPage fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} />}
        {activeTab === 'tutorials' && <TutorialsPage setActiveTab={setActiveTab} />}
        {/* Guides route removed — now a tab inside Settings (Phase 3) */}
        {activeTab === 'analytics' && (
          <TrendsAnalysis
            user={user}
            token={token}
            fetchWithAuth={fetchWithAuth}
            setActiveTab={setActiveTab}
            initialMainTab={analyticsInitialTab}
          />
        )}
          </>
        )}

        {/* PAGE FOOTER — Section 6: flex-shrink: 0, always visible */}
        <footer className="page-footer">
          <div className="footer-copyright">
            <span>{tenant.copyright_text || '© 2026 Pivotal Technologies, LLC'}</span>
            {tenant.is_default_tenant && <span>Precision AI Health Technology™</span>}
            <span><strong>Patent Pending</strong> — All rights reserved.</span>
          </div>
          <div className="footer-links">
            <a href={tenant.about_url || "/healist-about.html"}><strong>About {tenant.site_name}</strong></a>
            <a href={tenant.privacy_url || "/privacy-policy.html"}>Privacy Policy</a>
            <a href={tenant.terms_url || "/terms-of-service.html"}>Terms & Conditions</a>
            {/* Mirrors LoginForm — same WL rule: partner's own page if set, else Healist only. */}
            {(tenant.security_url || tenant.is_default_tenant) &&
              <a href={tenant.security_url || "/security.html"}>Security</a>}
            {tenant.is_default_tenant && <a href="/media-kit/">Press & Media</a>}
          </div>
        </footer>
      </main>

      {/* HEALTH SUMMARY MODAL */}
      {showHealthSummary && (
        <HealthSummaryModal
          onClose={() => setShowHealthSummary(false)}
          fetchWithAuth={fetchWithAuth}
          user={user}
        />
      )}

      {/* DIET COMPLIANCE CHECK MODAL (from Dashboard Quick Action) */}
      {showDietCheck && (
        <div className="modal-overlay" onClick={() => setShowDietCheck(false)}>
          <div onClick={e => e.stopPropagation()}>
            <DietComplianceChecker
              token={token}
              fetchWithAuth={fetchWithAuth}
              onClose={() => setShowDietCheck(false)}
              embedded={false}
            />
          </div>
        </div>
      )}

      {/* WHAT CAN I COOK MODAL */}
      {showWhatCanICook && (
        <WhatCanICook
          token={token}
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowWhatCanICook(false)}
          onViewRecipe={(recipe) => {
            setShowWhatCanICook(false);
            openWciRecipeView(recipe);
          }}
        />
      )}

      {/* WCI RECIPE VIEWER */}
      {wciRecipeView && (
        <div className="modal-overlay" onClick={closeWciRecipeView}>
          <div className="rv-modal" onClick={e => e.stopPropagation()}>
            <div className="rv-header">
              <h3 className="rv-title">{wciRecipeView.title}</h3>
              <button onClick={closeWciRecipeView} className="rv-closeBtn">&times;</button>
            </div>
            <div className="rv-content">
              {wciRecipeUrl ? (
                <iframe
                  src={wciRecipeUrl}
                  className="rv-iframe"
                  title={wciRecipeView.title}
                  data-testid="wci-recipe-iframe"
                />
              ) : (
                <div className="rv-loading" style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>
                  Loading recipe…
                </div>
              )}
            </div>
            <div className="rv-footer">
              <button onClick={closeWciRecipeView} className="rv-closeFooterBtn">Close</button>
              {wciRecipeView.file_path && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleWciDownloadFile(wciRecipeView)}
                  data-testid="wci-recipe-download-btn"
                >
                  Download File
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleWciPrint(wciRecipeView.id, wciRecipeView.title)}
              >
                Print
              </button>
              <button
                onClick={() => handleWciMakeRecipe(wciRecipeView.id, wciRecipeView.title)}
                className="rv-makeBtn"
              >
                Make Recipe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOG HYDRATION MODAL */}
      {showHydrationModal && (
        <LogHydrationModal
          onClose={() => setShowHydrationModal(false)}
          fetchWithAuth={fetchWithAuth}
          onSuccess={() => {
            setShowHydrationModal(false);
            // Dashboard stats will refresh on next render
          }}
        />
      )}

      {/* UNIFIED AI LIMIT MODAL (429 CREDITS_EXCEEDED / 403 FEATURE_NOT_AVAILABLE) */}
      <AILimitModal onUpgradeClick={() => setActiveTab('account')} />

      {/* #64 ONBOARDING INTERVIEW (first-login, server-gated) */}
      {onboardingOffer && (
        <OnboardingInterview
          fetchWithAuth={fetchWithAuth}
          offer={onboardingOffer}
          onComplete={() => setOnboardingOffer(null)}
          onSkip={() => setOnboardingOffer(null)}
          onDismiss={() => setOnboardingOffer(null)}
        />
      )}

      {/* PUSH PERMISSION — asked ONLY from the installed PWA, where iOS can grant it.
          Renders nothing in a browser tab; see PushEnableNudge for the full rationale. */}
      <PushEnableNudge push={pushNotifications} />

      {/* PWA INSTALL PROMPT */}
      {showPWAPrompt && (
        <PWAInstallPrompt
          onClose={() => setShowPWAPrompt(false)}
          onInstalled={() => {
            setShowPWAPrompt(false);
            // ⚠️ ONLY subscribe when we are actually running as an installed PWA.
            // On iOS "install" is a manual Add-to-Home-Screen flow, so this handler fires
            // in the SAFARI TAB — where iOS never grants Web Push. The old code called
            // subscribe() here unconditionally, it threw, and `.catch(console.error)`
            // swallowed it, leaving the iOS user with no prompt and no explanation.
            // From the home screen, PushEnableNudge above asks at a moment that works.
            if (pushNotifications.isSupported &&
                pushNotifications.isPWA &&
                !pushNotifications.isSubscribed) {
              pushNotifications.subscribe().catch((e) =>
                console.error('[push] post-install subscribe failed:', e));
            }
          }}
          fetchWithAuth={fetchWithAuth}
          API_BASE={API_BASE}
        />
      )}
    </div>
  );
}

// Main App Component
function App() {
  const [authMode, setAuthMode] = useState('login');
  const [betaSignupEmail, setBetaSignupEmail] = useState('');   // #83 — prefill from the beta gate
  const [referralInfo, setReferralInfo] = useState(null);

  // Check for referral code in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode && refCode.startsWith('HEALIST-')) {
      // Validate referral code via public API
      fetch(`${API_BASE}/api/billing/referral/validate/${refCode}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.valid) {
            setReferralInfo({
              code: refCode,
              referrerName: data.referrer_name,
              bonusTrialDays: data.bonus_trial_days,
            });
            setAuthMode('register');
            sessionStorage.setItem('healist_referral', JSON.stringify({
              code: refCode, referrerName: data.referrer_name,
              bonusTrialDays: data.bonus_trial_days,
            }));
          }
        })
        .catch(() => {}); // Silent fail — show normal login
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // Restore from sessionStorage (survives page reload during verification)
      const stored = sessionStorage.getItem('healist_referral');
      if (stored) {
        try {
          setReferralInfo(JSON.parse(stored));
        } catch { /* ignore */ }
      }
    }
  }, []);

  if (window.location.pathname === '/provider-portal') {
    return <ProviderPortal />;
  }

  if (window.location.pathname === '/trainer-portal') {
    return <TrainerPortal />;
  }

  return (
    <TenantProvider>
    <AuthProvider>
      <AuthContext.Consumer>
        {({ user, token, loading, isAuthenticated, logout }) => {
          if (loading) {
            return (
              <div className="flex items-center justify-center min-h-100vh">
                <div className="spinner"></div>
              </div>
            );
          }

          if (!isAuthenticated) {
            return authMode === 'login' ? (
              <LoginForm
                onToggleMode={() => setAuthMode('register')}
                onBetaSignup={(email) => { setBetaSignupEmail(email); setAuthMode('register'); }}
              />
            ) : (
              <RegisterForm
                onToggleMode={() => {
                  setAuthMode('login');
                  setReferralInfo(null);
                  setBetaSignupEmail('');
                  sessionStorage.removeItem('healist_referral');
                }}
                referralInfo={referralInfo}
                initialEmail={betaSignupEmail}
              />
            );
          }

          // Authenticated but the profile hasn't loaded yet (auth tokens are set by
          // authenticateWithTokens() before /auth/me populates `user`, and a transient
          // /auth/me failure can leave it null). NEVER fall through to the Dashboard in
          // that window — show a loader so the subscription gate can't be bypassed (#43).
          if (!user) {
            return (
              <div className="flex items-center justify-center min-h-100vh">
                <div className="spinner"></div>
              </div>
            );
          }

          // Hard-stop subscription gate (#43): authenticated but no active subscription.
          // Single source — backend-resolved user.is_subscriber (handles permanent_test_acct
          // + the paid-status set). FE never re-hardcodes the status list (#consistency P1/C).
          if (!user.is_subscriber) {
            return <SubscriptionGate user={user} token={token} onLogout={logout} />;
          }

          return <CreditSpendProvider><Dashboard /></CreditSpendProvider>;
        }}
      </AuthContext.Consumer>
    </AuthProvider>
    </TenantProvider>
  );
}

export default App;

